import { useMutation } from '@tanstack/react-query'
import { newsApi } from '../api/newsApi'
import { useNewsStore } from '../stores/newsStore'
import type { NewsSearchRequest } from '../types/news.types'
import { getErrorMessage } from '@/lib/axios'
import { searchHistory } from '../utils/searchHistory'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// News Search Hook with History Integration
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function useNewsSearch() {
  const { setTabData, setTabError, setTabLoading } = useNewsStore()

  return useMutation({
    mutationFn: (request: NewsSearchRequest) => newsApi.searchNews(request),

    onMutate: (variables) => {
      // Set loading state for all keywords
      variables.keywords.forEach((keyword) => {
        setTabLoading(keyword, true)
      })
    },

    onSuccess: (data, variables) => {
      // Update store with results for each keyword
      data.results.forEach((result) => {
        setTabData(result.keyword, result.items, result.total)
      })

      // Handle keywords with no results
      variables.keywords.forEach((keyword) => {
        const hasResult = data.results.some((r) => r.keyword === keyword)
        if (!hasResult) {
          setTabData(keyword, [], 0)
        }
      })

      // Add to search history
      searchHistory.add(variables.keywords, {
        display: variables.display || 10,
        sort: variables.sort || 'sim',
      })
    },

    onError: (error, variables) => {
      const message = getErrorMessage(error)

      // Set error state for all keywords
      variables.keywords.forEach((keyword) => {
        setTabError(keyword, message)
      })
    },
  })
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Keyword News Hook (for pagination)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface UseKeywordNewsOptions {
  display?: number
  sort?: 'sim' | 'date'
}

interface FetchPageVars {
  keyword: string
  page: number
}

/**
 * Fetches one page for one keyword.
 *
 * Both the keyword and the page are mutation variables rather than values captured
 * at render time. Previously the keyword was bound to `activeKeyword` and the page
 * was read from `tabs[keyword].currentPage` inside `mutationFn`, so calling
 * `setTabPage(n)` and then `mutate()` synchronously re-fetched the *previous* page —
 * clicking 3 loaded 2 — and a page change on a non-active tab fetched the wrong
 * keyword entirely.
 */
export function useKeywordNews(options: UseKeywordNewsOptions = {}) {
  const { setTabData, setTabError, setTabLoading } = useNewsStore()
  const { display = 10, sort = 'sim' } = options

  return useMutation({
    mutationFn: ({ keyword, page }: FetchPageVars) =>
      newsApi.getPaginatedNews(keyword, page, display, sort),

    onMutate: ({ keyword }) => {
      setTabLoading(keyword, true)
    },

    onSuccess: (data, { keyword }) => {
      setTabData(keyword, data.items, data.total)
    },

    onError: (error, { keyword }) => {
      setTabError(keyword, getErrorMessage(error))
    },
  })
}
